<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Turnos de {{ plate }}</h1>
      <b-button
        variant="none"
        class="button button-light"
        :to="{ name: 'config-vehicle-list' }"
      >
        Volver
      </b-button>
    </div>

    <div class="table-container">
      <!-- Filtros -->
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-center">
          <div class="d-flex align-items-center mr-2 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2">Desde</label>
            <input type="date" v-model="from" class="toolbar-select" />
          </div>
          <div class="d-flex align-items-center mr-2 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2">Hasta</label>
            <input type="date" v-model="to" class="toolbar-select" />
          </div>
          <b-button
            variant="none"
            class="button button-primary mb-2 mb-md-0"
            @click="loadData"
          >
            Filtrar
          </b-button>
        </div>
      </div>

      <!-- Tabla de turnos -->
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Inicio</th>
              <th>Fin</th>
              <th>Conductor</th>
              <th>Tipo</th>
              <th>Foto</th>
              <th>Duración</th>
              <th>Km Inicio</th>
              <th>Km Fin</th>
              <th>Delta Km</th>
              <th class="pr-md-4">Recorrido</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.shift_id">
              <td class="text-center pl-3">{{ formatDateTime(o.started_at) }}</td>
              <td class="text-center">
                <span v-if="o.ended_at">{{ formatDateTime(o.ended_at) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">{{ o.driver_name }}</td>
              <td class="text-center">
                <span :class="`badge ${o.shift_type === 'INICIO' ? 'badge-success' : 'badge-info'}`">
                  {{ o.shift_type }}
                </span>
              </td>
              <td class="text-center">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold"
                  v-b-tooltip.hover
                  title="Ver foto de inicio"
                  @click="openPhoto(o)"
                >
                  <i class="ri-camera-line"></i> Ver
                </button>
              </td>
              <td class="text-center">
                <span v-if="!o.ended_at" class="badge badge-amber">En curso</span>
                <span v-else>{{ durationText(o) }}</span>
              </td>
              <td class="text-center">
                <button
                  v-if="o.start_odometer_km != null"
                  type="button"
                  class="km-link"
                  v-b-tooltip.hover
                  title="Ver foto del odómetro de inicio"
                  @click="openOdometerPhoto(o, 'start')"
                >
                  {{ formatKm(o.start_odometer_km) }}
                </button>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <button
                  v-if="o.end_odometer_km != null"
                  type="button"
                  class="km-link"
                  v-b-tooltip.hover
                  title="Ver foto del odómetro de fin"
                  @click="openOdometerPhoto(o, 'end')"
                >
                  {{ formatKm(o.end_odometer_km) }}
                </button>
                <span v-else-if="o.end_type === 'AUTO_CLOSED'" class="text-muted">Auto-cierre</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <span v-if="o.start_odometer_km != null && o.end_odometer_km != null">{{ deltaKmText(o) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center pr-md-4">
                <button
                  type="button"
                  class="button-tooltip text-primary font-weight-bold"
                  v-b-tooltip.hover
                  title="Ver recorrido del turno"
                  @click="goToRoute(o)"
                >
                  <i class="ri-route-line"></i> Ver recorrido
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <div class="table-no-results" v-if="!list.length">
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <!-- Modal de foto de inicio de turno -->
    <b-modal v-model="showPhoto" size="lg" hide-footer title="Foto de inicio de turno">
      <div v-if="selectedShift">
        <div class="photo-info d-flex flex-wrap mb-3">
          <div class="photo-info__item">
            <span class="photo-info__label">Conductor</span>
            <span class="photo-info__value">{{ selectedShift.driver_name }}</span>
          </div>
          <div class="photo-info__item">
            <span class="photo-info__label">Inicio</span>
            <span class="photo-info__value">{{ formatDateTime(selectedShift.started_at) }}</span>
          </div>
          <div class="photo-info__item">
            <span class="photo-info__label">Tipo</span>
            <span class="photo-info__value">{{ selectedShift.shift_type }}</span>
          </div>
          <div class="photo-info__item">
            <span class="photo-info__label">Coordenadas GPS</span>
            <span class="photo-info__value">
              {{ coordsText(selectedShift.start_lat, selectedShift.start_lng) }}
            </span>
          </div>
        </div>

        <div class="photo-frame text-center">
          <img v-if="photoUrl" :src="photoUrl" alt="Foto de inicio de turno" class="photo-img" />
          <p v-else-if="photoError" class="text-muted m-0 py-4">{{ photoError }}</p>
          <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
        </div>
      </div>
    </b-modal>

    <!-- Modal de foto del odómetro (inicio/fin) -->
    <b-modal v-model="showOdometer" size="lg" hide-footer :title="odometerLabel">
      <div class="photo-info d-flex flex-wrap mb-3">
        <div class="photo-info__item">
          <span class="photo-info__label">Lectura</span>
          <span class="photo-info__value">{{ odometerReading }}</span>
        </div>
      </div>
      <div class="photo-frame text-center">
        <img v-if="odometerUrl" :src="odometerUrl" alt="Foto del odómetro" class="photo-img" />
        <p v-else-if="odometerError" class="text-muted m-0 py-4">{{ odometerError }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { ActivityLogService, VehicleService } from '@/core/services'

function today() {
  return new Date().toISOString().slice(0, 10)
}

export default {
  name: 'VehicleShiftsPage',
  data() {
    return {
      vehicleId: this.$route.params.id,
      plate: '',
      list: [],
      from: today(),
      to: today(),
      showPhoto: false,
      selectedShift: null,
      photoUrl: null,
      photoError: null,
      showOdometer: false,
      odometerLabel: '',
      odometerReading: '—',
      odometerUrl: null,
      odometerError: null
    }
  },
  computed: {
    // Timezone IANA de la ciudad activa; los timestamps se guardan en UTC y se
    // convierten para mostrar. Fallback a America/Lima si no hay ciudad activa.
    cityTz() {
      const city = this.$store.getters['city/getCurrentCity']
      return city && city.timezone ? city.timezone : 'America/Lima'
    }
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleString('es-PE', {
        timeZone: this.cityTz,
        day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      })
    },
    // Duración fin - inicio, en "Xh Ym".
    durationText(shift) {
      if (!shift.ended_at) return 'En curso'
      const start = new Date(shift.started_at).getTime()
      const end = new Date(shift.ended_at).getTime()
      if (isNaN(start) || isNaN(end) || end < start) return '—'
      const minutes = Math.round((end - start) / 60000)
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return h ? `${h}h ${m}m` : `${m}m`
    },
    coordsText(lat, lng) {
      if (lat == null || lng == null) return '—'
      return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
    },
    // Navega a la vista de recorrido del turno (DEF-ADM-013).
    goToRoute(shift) {
      this.$router.push({ name: 'shift-route', params: { id: shift.shift_id } })
    },
    // Odómetro (DEF-ADM-014): formato con separador de miles; null → "—".
    formatKm(v) {
      if (v == null) return '—'
      return Number(v).toLocaleString('en-US')
    },
    deltaKmText(shift) {
      if (shift.start_odometer_km == null || shift.end_odometer_km == null) return '—'
      const delta = shift.end_odometer_km - shift.start_odometer_km
      return `${Number(delta).toLocaleString('en-US')} km`
    },
    // Abre la foto del odómetro (type = 'start' | 'end') en un modal.
    async openOdometerPhoto(shift, type) {
      const km = type === 'start' ? shift.start_odometer_km : shift.end_odometer_km
      this.odometerLabel = type === 'start' ? 'Odómetro de inicio' : 'Odómetro de fin'
      this.odometerReading = km != null ? `${Number(km).toLocaleString('en-US')} km` : '—'
      this.odometerUrl = null
      this.odometerError = null
      this.showOdometer = true
      try {
        const response = await ActivityLogService.getOdometerPhoto(shift.shift_id, type)
        this.odometerUrl = response.payload ? response.payload.url : null
        if (!this.odometerUrl) this.odometerError = 'Este turno no tiene foto de odómetro.'
      } catch (e) {
        console.error(e)
        this.odometerError = e.errors ? e.errors.message : 'No se pudo cargar la foto del odómetro.'
      }
    },
    async loadVehicle() {
      try {
        const response = await VehicleService.get(this.vehicleId)
        this.plate = response.payload ? response.payload.license_plate : ''
      } catch (e) {
        console.error(e)
      }
    },
    async loadData() {
      if (!this.from || !this.to) {
        Alert.error('Selecciona el rango de fechas')
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ActivityLogService.getVehicleShifts(this.vehicleId, this.from, this.to)
        this.list = response.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener los turnos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    // El backend devuelve una URL presigned (JSON): se usa directo en el <img>,
    // porque la presigned se autentica sola (no requiere el JWT).
    async openPhoto(shift) {
      this.selectedShift = shift
      this.photoUrl = null
      this.photoError = null
      this.showPhoto = true
      try {
        const response = await ActivityLogService.getShiftPhoto(shift.shift_id)
        this.photoUrl = response.payload ? response.payload.url : null
        if (!this.photoUrl) this.photoError = 'Este turno no tiene foto disponible.'
      } catch (e) {
        console.error(e)
        this.photoError = e.errors ? e.errors.message : 'No se pudo cargar la foto.'
      }
    }
  },
  async mounted() {
    await this.loadVehicle()
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.filter-label
  font-size 13px
  color #6c757d
  white-space nowrap

.badge-amber
  background-color #f59e0b
  color #fff

.km-link
  background none
  border none
  padding 0
  color #007bff
  font-weight 600
  cursor pointer
  text-decoration underline dotted
  &:hover
    color #0056b3

.photo-info__item
  display flex
  flex-direction column
  min-width 170px
  margin-right 24px
  margin-bottom 8px

.photo-info__label
  font-size 11px
  text-transform uppercase
  letter-spacing .06em
  color #8592A6

.photo-info__value
  font-size 14px
  font-weight 600
  margin-top 2px

.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden

.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
